import { useCallback, useEffect, useRef } from "react";

import { useAnnotationStore } from "@/stores/annotation-store";
import { useSlideStore } from "@/stores/slide-store";

export type AnnotationRole = "presenter" | "viewer";

interface UseSlideAnnotationsOptions {
  // Scopes the BC channel: `optidev-slide-sync:{projectId}`. If null /
  // undefined the hook becomes a no-op (no channel created), so it's
  // safe to call before the project loads.
  projectId: string | null | undefined;
  // "presenter" surfaces SEND outgoing strokes + receive peer updates.
  // "viewer"   surfaces only receive — the returned callbacks become
  //            no-ops so wiring them by mistake doesn't broadcast.
  role: AnnotationRole;
}

interface UseSlideAnnotationsReturn {
  // Wire to AnnotationOverlay's `onChange` prop on presenter surfaces.
  // Broadcasts an `annotation-update` to all BC peers carrying the full
  // SVG dump for the current slide. Deliberately does NOT write to the
  // local annotation store — see use-slide-annotations.ts for the
  // self-reload-feedback-loop note that broke per-stroke laser fade.
  onChange: (svg: string) => void;
  // Wire to AnnotationToolbar's `onClear` prop. Mutates the local store
  // (so the current surface's overlay reacts via its liveSnapshot
  // subscription) AND broadcasts an `annotation-clear` to peers.
  onClear: () => void;
}

// Annotation BC bridge for any surface that displays / sources slide
// annotations. Single owner of the annotation message handlers; replaces
// the per-surface `handleAnnotationChange` / `handleAnnotationClear`
// callbacks that used to live inline in PresenterDashboard and
// IntegratedEditorWithAgent.
//
// On every surface:
//   - Opens a BroadcastChannel named `optidev-slide-sync:{projectId}`.
//   - Listens for inbound `annotation-update` / `annotation-clear`
//     messages → writes to the annotation store. AnnotationOverlay
//     instances mounted on the same surface react via their
//     `liveSnapshot` subscription (no imperative call needed here).
//
// For presenter surfaces only:
//   - Returns `onChange(svg)` and `onClear()` callbacks that broadcast
//     outbound messages. Viewers get the same shape but the bodies
//     short-circuit on role !== "presenter" so passing them by mistake
//     to a render-only overlay is a safe no-op.
//
// Multiple BroadcastChannels of the same name coexist cleanly per
// MDN — each channel receives messages from peer channels (including
// other channels in the same tab) but never echoes back to its own
// sender. So this hook can coexist with the editor's existing
// slide-sync channel that handles `slide-navigate` /
// `presentation-config-update` / `end-presentation`.
export function useSlideAnnotations({
  projectId,
  role,
}: UseSlideAnnotationsOptions): UseSlideAnnotationsReturn {
  const bcRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const bc = new BroadcastChannel(`optidev-slide-sync:${projectId}`);
    bcRef.current = bc;
    bc.onmessage = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      const data = e.data as { type?: string };
      if (data.type === "annotation-update") {
        const payload = e.data as {
          slideIndex: number;
          svg: string;
        };
        useAnnotationStore
          .getState()
          .saveSlideSnapshot(payload.slideIndex, payload.svg);
      } else if (data.type === "annotation-clear") {
        const payload = e.data as { slideIndex: number };
        useAnnotationStore.getState().clearSlide(payload.slideIndex);
      }
    };
    return () => {
      bc.close();
      bcRef.current = null;
    };
  }, [projectId]);

  const onChange = useCallback(
    (svg: string) => {
      if (role !== "presenter") return;
      const slideIndex = useSlideStore.getState().currentSlideIndex;
      bcRef.current?.postMessage({
        type: "annotation-update",
        slideIndex,
        svg,
      });
    },
    [role],
  );

  const onClear = useCallback(() => {
    if (role !== "presenter") return;
    const slideIndex = useSlideStore.getState().currentSlideIndex;
    useAnnotationStore.getState().clearSlide(slideIndex);
    bcRef.current?.postMessage({
      type: "annotation-clear",
      slideIndex,
    });
  }, [role]);

  return { onChange, onClear };
}
